import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

import { GIFileType } from '@enums/gi-file-type.enum';
import { GIFileForm } from '@forms/gi-file-form';
import { PrestationForm } from '@gi/forms/prestation-form';
import { ContractOffer } from '@gi/models/contract-offer';
import { Prestation } from '@gi/models/prestation';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { GIFile } from '@models/gi-file';
import { ModelOffer } from '@models/model-offer';

export type WithPrestationsAndFilesFormBaseType = {
  prestations: FormArray<PrestationForm>;
  files: FormArray<GIFileForm>;
  filesToDelete: FormArray<GIFileForm>;
};

export function getWithPrestationsAndFilesFormBaseProps(): WithPrestationsAndFilesFormBaseType {
  return {
    prestations: new FormArray<PrestationForm>([new PrestationForm()]),
    files: new FormArray<GIFileForm>([new GIFileForm()]),
    filesToDelete: new FormArray<GIFileForm>([])
  };
}

export class WithPrestationsAndFilesBaseForm<
  TControl extends { [K in keyof TControl]?: AbstractControl<any> } = any
> extends FormGroup<TControl & WithPrestationsAndFilesFormBaseType> {
  constructor(additionalControls: TControl = {} as TControl) {
    super({ ...getWithPrestationsAndFilesFormBaseProps(), ...additionalControls } as TControl &
      WithPrestationsAndFilesFormBaseType);
  }

  get prestations(): FormArray<PrestationForm> {
    return this.controls.prestations as FormArray<PrestationForm>;
  }

  get prestationsErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.prestations, 'Veuillez ajouter au minimum une prestation');
  }

  get files(): FormArray<GIFileForm> {
    return this.controls.files as FormArray<GIFileForm>;
  }

  get filesToDelete(): FormArray<GIFileForm> {
    return this.controls.filesToDelete as FormArray<GIFileForm>;
  }

  removePrestation(index: number): void {
    this.prestations.removeAt(index);
  }

  addPrestation(prestation: Prestation): void {
    const prestationForm = new PrestationForm();
    prestationForm.fromPrestation(prestation);
    this.prestations.push(prestationForm);
  }

  addPrestationsFromModelOffer(modelOffer: ModelOffer): void {
    modelOffer.prestations.forEach(prestation => {
      prestation.id = null;
      prestation.contractOffer = ContractOffer.fromModelOffer(modelOffer);
      this.addPrestation(prestation);
    });
    this.prestations.controls = [...this.prestations.controls];
  }

  addFilesFromModelOffer(modelOffer: ModelOffer): void {
    modelOffer.files.forEach((file: GIFile) => {
      file.contractOffer = ContractOffer.fromModelOffer(modelOffer);
      this.addFile(file);
    });
  }

  addFile(file: GIFile): void {
    const fileForm = new GIFileForm();
    fileForm.fromFile(file);
    this.files.push(fileForm);
  }

  removeFile(fileIndex: number): void {
    const fileForm: GIFileForm = this.files.controls.find((_, index: number) => index === fileIndex);

    if (fileForm?.id.value) {
      this.addFileToDelete(fileForm);
    }

    this.files.removeAt(fileIndex);
  }

  addFileToDelete(fileForm: GIFileForm): void {
    this.filesToDelete.push(fileForm);
  }

  addFileToUpload(file: File): void {
    const fileForm = new GIFileForm();
    fileForm.name.setValue(file.name);
    fileForm.type.setValue(GIFileType.other);
    fileForm.content.setValue(file);
    this.files.push(fileForm);
  }

  getFilesToUpload(): { title: string; content: File; type: string }[] {
    return this.files.controls
      .filter((fileForm: GIFileForm) => !fileForm.id.value && !fileForm.contractOffer.value && fileForm.content.value)
      .map((fileForm: GIFileForm) => ({
        title: fileForm.title.value,
        content: fileForm.content.value,
        type: fileForm.type.value
      }));
  }

  getFilesToDelete(): number[] {
    return this.filesToDelete.controls.map((fileForm: GIFileForm) => fileForm.id.value);
  }
}
